import React, { useState, useEffect } from 'react'
import { locationTagColumn } from '../../datatablesource'
import { locationTagInputs } from '../../formSource'
import Datatable from '../datatable/Datatable'
import "./locationTags.scss"
import Barcode from 'react-barcode';
import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";
import axios from 'axios';
import baseUrl from '../../utils/config';
import Cookies from 'js-cookie';
import newRequest from '../../utils/newRequest';
import logoImage from '../../images/HD GST LOGO.png';

const LocationTags = () => {
  const { t, i18n } = useTranslation();
  
  const [ctr, setCtr] = useState('');
  const [ctr2, setCtr2] = useState('');
  const [shortName, setShortName] = useState('');
  const [noOfLabels, setNoOfLabels] = useState('');
  const [tableData, setTableData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null); // 'success' or 'error'

  // API base URL - using localhost:7001 as specified
  const API_BASE_URL = baseUrl;

  // Fetch location tags from API
  const fetchLocationTags = async (searchQuery = '') => {
    setIsLoading(true);
    try {
      const url = searchQuery 
        ? `${API_BASE_URL}/SearchLocationTag?searchTerm=${encodeURIComponent(searchQuery)}`
        : `${API_BASE_URL}/SearchLocationTag`;
      
      // Get token from sessionStorage or Cookies (fallback)
      const token = sessionStorage.getItem('accessToken') || Cookies.get('accessToken');
      
      // Create axios request with authentication header
      const config = {
        headers: {}
      };
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn('No authentication token found. Please login again.');
      }
      
      const response = await axios.get(url, config);
      
      // Map API response to table format
      const mappedData = response.data.map((item, index) => ({
        id: item.id || `api-${index + 1}`,
        tagNo: item.ctrNo || '',
        name: item.shortName || item.longName || '',
        longName: item.longName || '',
        // Keep original data for reference
        originalData: item,
        isGenerated: false // Mark existing entries as not generated
      }));
      
      setTableData(mappedData);
    } catch (error) {
      console.error('Error fetching location tags:', error);
      // Optionally show error message to user
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data on component mount and when search term changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchLocationTags(searchTerm);
    }, 300); // Debounce search by 300ms

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Handle bulk save to API
  const handleBulkSave = async () => {
    // Filter only generated entries (isGenerated: true)
    const generatedEntries = tableData.filter(row => row.isGenerated === true);
    
    if (!generatedEntries || generatedEntries.length === 0) {
      setMessage(t('No generated entries to save. Please generate entries first.'));
      setMessageType('error');
      setTimeout(() => {
        setMessage(null);
        setMessageType(null);
      }, 3000);
      return;
    }

    setIsSaving(true);
    setMessage(null);
    setMessageType(null);

    try {
      // Map only generated entries to API format
      const apiPayload = generatedEntries.map((row) => {
        // Use the full tagNo as ctrNo to ensure uniqueness (e.g., "1233-1", "1233-2")
        // This ensures each entry has a unique ctrNo value
        const ctrNoValue = row.tagNo ? String(row.tagNo) : ctr || '';
        
        // Get shortName and longName from row or form fields
        const shortNameValue = row.name || shortName || '';
        const longNameValue = ctr2 || '';
        
        // Extract base ctrNo for noLabel construction (part before "-")
        const tagNoParts = row.tagNo ? String(row.tagNo).split('-') : [];
        const baseCtrNo = tagNoParts[0] || ctr || '';
        
        // Construct noLabel based on example format "12khN"
        // Format: baseCtrNo + first 2 lowercase chars of shortName + last char uppercase of shortName
        let noLabelValue = '';
        if (baseCtrNo && shortNameValue) {
          const shortNameAbbr = shortNameValue.substring(0, 2).toLowerCase();
          const lastChar = shortNameValue.length > 0 
            ? shortNameValue.charAt(shortNameValue.length - 1).toUpperCase() 
            : '';
          noLabelValue = `${baseCtrNo}${shortNameAbbr}${lastChar}`;
        } else {
          // Fallback to tagNo if construction fails
          noLabelValue = row.tagNo || '';
        }
        
        return {
          ctrNo: ctrNoValue, // Full tagNo to ensure uniqueness
          shortName: shortNameValue,
          longName: longNameValue,
          noLabel: noLabelValue
        };
      });

      const token = sessionStorage.getItem('accessToken') || Cookies.get('accessToken');
      const config = {
        headers: {
          'Content-Type': 'application/json'
        }
      };
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.post(
        `${API_BASE_URL}/PostLocationTag`,
        apiPayload,
        config
      );

      if (response.data && response.data.message) {
        setMessage(`${response.data.message} (${response.data.count || generatedEntries.length} entries saved)`);
        setMessageType('success');
        
        // Remove generated entries from tableData after successful save
        // Keep only non-generated entries (existing entries from API)
        const remainingData = tableData.filter(row => row.isGenerated !== true);
        setTableData(remainingData);
        
        // Refresh the table data from API after successful save
        // This will fetch all entries including the newly saved ones
        setTimeout(() => {
          fetchLocationTags(searchTerm);
        }, 1000);
      }
    } catch (error) {
      console.error('Error saving location tags:', error);
      setMessage(error.response?.data?.message || t('Error saving location tags. Please try again.'));
      setMessageType('error');
    } finally {
      setIsSaving(false);
      setTimeout(() => {
        setMessage(null);
        setMessageType(null);
      }, 5000);
    }
  };


  // Handle Generate button click
  const handleGenerate = async () => {
    if (!ctr) {
      setMessage(t('Please enter Ctr No'));
      setMessageType('error');
      setTimeout(() => {
        setMessage(null);
        setMessageType(null);
      }, 3000);
      return;
    }

    // Prevent multiple simultaneous calls
    if (isGenerating) {
      return;
    }

    setIsGenerating(true);
    setMessage(null);
    setMessageType(null);

    try {
      const token = sessionStorage.getItem('accessToken') || Cookies.get('accessToken');
      const config = {
        headers: {},
        timeout: 10000 // 10 second timeout to prevent hanging
      };
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.get(
        `${API_BASE_URL}/CheckLocationTagByCtrNo/${ctr}`,
        config
      );

      if (response.data.exists) {
        // ctrNo already exists - show message
        setMessage(t('This ctrNo already exists'));
        setMessageType('error');
        
        // Optionally, you can show the existing data
        if (response.data.data && response.data.data.length > 0) {
          const existingData = response.data.data.map((item, index) => ({
            id: item.id || `existing-${index + 1}`,
            tagNo: item.ctrNo || '',
            name: item.shortName || item.longName || '',
            longName: item.longName || '',
            originalData: item,
            isGenerated: false
          }));
          setTableData(existingData);
        }
      } else {
        // ctrNo doesn't exist - auto-generate entries based on "No of Labels" input
        if (!shortName || !noOfLabels) {
          setMessage(t('Please fill in Short Name and No of Labels to generate entries'));
          setMessageType('error');
          setIsGenerating(false);
        } else {
          // Generate entries based on "No of Labels" input field
          const numLabels = Number(noOfLabels) || 0;
          
          // Validate the number of labels
          if (numLabels <= 0 || numLabels > 1000) {
            setMessage(t('No of Labels must be between 1 and 1000'));
            setMessageType('error');
            setIsGenerating(false);
            return;
          }
          
          // Generate entries - always start from 1
          const labelStartingNo = 1;
          const timestamp = Date.now();
          
          // Use Array.from for better performance
          const generatedData = Array.from({ length: numLabels }, (_, i) => ({
            id: `gen-${timestamp}-${i}`,
            tagNo: `${ctr}-${labelStartingNo + i}`,
            name: shortName,
            longName: ctr2 || '', // Store longName in the row data
            isGenerated: true
          }));

          // Optimize filtering - use a more efficient approach
          // Use setTimeout to defer state update and prevent blocking
          setTimeout(() => {
            setTableData(prevData => {
              const baseCtrNo = String(ctr);
              // Filter more efficiently - only check generated items
              const filteredData = prevData.filter(item => {
                if (!item.isGenerated) return true;
                if (item.tagNo) {
                  const itemBase = String(item.tagNo).split('-')[0];
                  return itemBase !== baseCtrNo;
                }
                return false;
              });
              
              // Put generated entries at the top (before existing entries)
              return generatedData.concat(filteredData);
            });

            setMessage(t(`${numLabels} entries generated successfully`));
            setMessageType('success');
            setIsGenerating(false);
          }, 0); // Defer to next event loop tick
        }
      }
    } catch (error) {
      console.error('Error checking ctrNo:', error);
      setMessage(error.response?.data?.message || t('Error checking ctrNo. Please try again.'));
      setMessageType('error');
      setIsGenerating(false);
    }
    
    // Clear message after timeout
    setTimeout(() => {
      setMessage(null);
      setMessageType(null);
    }, 5000);
  };

  const handleRowPrint = (rowId, rowDataParam = null) => {
    // Use rowDataParam if provided, otherwise find it
    let rowData = rowDataParam;
    
    if (!rowData) {
      // Try to find by ID first
      rowData = tableData.find(row => String(row.id) === String(rowId));
      
      // If not found, try finding by index (in case ID doesn't match)
      if (!rowData) {
        const rowIndex = parseInt(rowId);
        if (!isNaN(rowIndex) && tableData[rowIndex - 1]) {
          rowData = tableData[rowIndex - 1];
        }
      }
    }
    
    if (!rowData) {
      console.error('Row data not found for id:', rowId);
      console.log('Available tableData:', tableData);
      alert(`Row data not found for ID: ${rowId}. Please try again.`);
      return;
    }

    if (!rowData.tagNo) {
      alert('Tag number is missing. Cannot print.');
      return;
    }

    // Calculate barcode value
    const tagNoParts = rowData.tagNo ? String(rowData.tagNo).split('-') : [];
    const ctrNoValue = tagNoParts[0] || '';
    const digitalValue = tagNoParts[1] || '';
    const shortNameValue = rowData.name || shortName || '';
    const barcodeValue = shortNameValue && ctrNoValue && digitalValue
      ? `${shortNameValue.toUpperCase()}-${ctrNoValue}-${digitalValue}`
      : shortNameValue && ctrNoValue
      ? `${shortNameValue.toUpperCase()}-${ctrNoValue}`
      : String(rowData.tagNo || '').toUpperCase();
    
    console.log('Printing row:', rowData);
    console.log('Barcode value to print:', barcodeValue);

    // Wait a bit to ensure React has rendered the barcode elements
    setTimeout(() => {
      const printWindow = window.open('', 'Print Window', 'height=400,width=800');
      
      if (!printWindow) {
        alert('Please allow popups to print.');
        return;
      }

      const styles = `
      @page { 
        size: 3in 2in; 
        margin: 0; 
      }
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }
      body { 
        font-size: 12px; 
        line-height: 1.2;
        font-family: Arial, sans-serif;
        margin: 0;
        padding: 0;
        background: white;
      }
      #barcode-wrapper { 
        display: flex; 
        flex-direction: column; 
        justify-content: flex-start; 
        align-items: flex-start; 
        padding: 10px;
        width: 100%;
        min-height: 0;
        height: auto;
      }
      .label-card {
        display: flex;
        flex-direction: column;
        width: 100%;
        padding: 0;
        position: relative;
        page-break-inside: avoid;
        break-inside: avoid;
      }
      .label-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        width: 100%;
        margin-bottom: 4px;
      }
      .label-logo {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: flex-start;
      }
      .label-logo img {
        max-width: 115px;
        max-height: 65px;
        width: auto;
        height: auto;
        object-fit: contain;
      }
      .label-qrcode {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-left: 8px;
      }
      .label-qrcode svg {
        width: 60px;
        height: 60px;
      }
      .label-separator {
        width: calc(100% + 24px);
        margin-left: -12px;
        margin-right: -12px;
        height: 0;
        border: none;
        border-top: 2px solid #000000;
        background-color: #000000 !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        margin-top: 4px;
        margin-bottom: 6px;
      }
      .label-barcode-container {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        overflow: visible;
        min-height: 45px;
        margin-bottom: 4px;
      }
      .label-barcode-container svg {
        width: auto !important;
        max-width: 100% !important;
        height: auto !important;
        min-width: 200px !important;
        min-height: 40px !important;
        max-height: 45px !important;
        display: block !important;
        margin: 0 auto !important;
      }
      .label-barcode-container canvas {
        width: auto !important;
        height: auto !important;
        min-height: 40px !important;
        max-height: 45px !important;
        display: block !important;
        margin: 0 auto !important;
      }
      .label-code {
        font-size: 12px;
        font-weight: normal;
        font-family: Arial, sans-serif;
        color: #000000;
        text-transform: uppercase;
        letter-spacing: 1px;
        text-align: center;
        width: 100%;
        margin-top: 2px;
      }
      svg {
        display: block;
      }
      @media print {
        body {
          margin: 0;
          padding: 0;
          background: white;
        }
        #barcode-wrapper {
          padding: 10px;
          min-height: 0 !important;
          height: auto !important;
        }
        #barcode-wrapper > div {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          page-break-after: avoid !important;
        }
        .label-card {
          display: flex !important;
          flex-direction: column !important;
          width: 100% !important;
          padding: 0 !important;
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        .label-header {
          display: flex !important;
          align-items: flex-start !important;
          justify-content: space-between !important;
          width: 100% !important;
          margin-bottom: 4px !important;
        }
        .label-logo {
          display: flex !important;
          flex: 1 !important;
          align-items: center !important;
          justify-content: flex-start !important;
        }
        .label-logo img {
          margin-top: 10px;
          max-width: 115px !important;
          max-height: 75px !important;
          width: auto !important;
          height: auto !important;
          object-fit: contain !important;
        }
        .label-qrcode {
          display: flex !important;
          flex-shrink: 0 !important;
          align-items: center !important;
          justify-content: center !important;
          margin-left: 8px !important;
        }
        .label-qrcode svg {
          width: 60px !important;
          height: 60px !important;
        }
        .label-separator {
          width: calc(100% + 24px) !important;
          margin-left: -12px !important;
          margin-right: -12px !important;
          height: 0 !important;
          border: none !important;
          border-top: 2px solid #000000 !important;
          background-color: #000000 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          margin-top: 4px !important;
          margin-bottom: 6px !important;
        }
        .label-barcode-container {
          width: 100% !important;
          min-height: 45px !important;
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
          margin-bottom: 4px !important;
        }
        .label-barcode-container svg {
          width: auto !important;
          max-width: 100% !important;
          height: auto !important;
          min-width: 200px !important;
          min-height: 40px !important;
          max-height: 45px !important;
          display: block !important;
          margin: 0 auto !important;
        }
        .label-barcode-container canvas {
          width: auto !important;
          height: auto !important;
          min-height: 40px !important;
          max-height: 45px !important;
          display: block !important;
          margin: 0 auto !important;
        }
        .label-code {
          font-size: 12px !important;
          font-weight: normal !important;
          text-align: center !important;
          text-transform: uppercase !important;
          margin-top: 2px !important;
        }
      }
    `;

      // Clone the entire source element to get all React-rendered content
      // Use the rowData.id to find the correct element
      const rowIdStr = String(rowData.id || rowId);
      const sourceId = `barcode-${rowIdStr}`;
      let source = document.getElementById(sourceId);
      
      // If not found, try alternative formats
      if (!source) {
        source = document.getElementById(`barcode-${rowId}`);
      }
      if (!source) {
        // Try to find by tagNo as fallback - find element with matching tagNo
        const allBarcodeElements = document.querySelectorAll('[id^="barcode-"]');
        for (let el of allBarcodeElements) {
          const elId = el.id.replace('barcode-', '');
          const elRow = tableData.find(r => String(r.id) === elId);
          if (elRow && elRow.tagNo === rowData.tagNo && elRow.name === rowData.name) {
            source = el;
            console.log('Found element by tagNo match:', elId, rowData.tagNo);
            break;
          }
        }
      }
      
      if (!source) {
        console.error('Barcode element not found for:', {
          rowId: rowId,
          rowDataId: rowData.id,
          sourceId: sourceId,
          tagNo: rowData.tagNo,
          name: rowData.name
        });
      }
      
      let printContent = '';
      
      // Extract shortname, ctrNo, and digital part for display
      const tagNoParts = rowData.tagNo ? String(rowData.tagNo).split('-') : [];
      const ctrNoValue = tagNoParts[0] || '';
      const digitalValue = tagNoParts[1] || ''; // The number after the dash (e.g., "1", "2")
      const shortNameValue = rowData.name || shortName || '';
      // Format: SHORTNAME-CTRNO-DIGITAL (e.g., "PILLO-12233-1")
      const displayCode = shortNameValue && ctrNoValue && digitalValue
        ? `${shortNameValue.toUpperCase()}-${ctrNoValue}-${digitalValue}`
        : shortNameValue && ctrNoValue
        ? `${shortNameValue.toUpperCase()}-${ctrNoValue}`
        : (rowData.tagNo || '').toUpperCase();
      
      // Get longName for display at top
      const longNameValue = rowData.longName || ctr2 || rowData.name || '';
      
      if (source) {
        // Clone the entire element including all React-rendered SVGs
        const cloned = source.cloneNode(true);
        cloned.classList.remove('hidden');
        cloned.id = `print-${sourceId}`; // Change ID to avoid conflicts
        
        // Make sure all nested elements are visible
        const allHidden = cloned.querySelectorAll('.hidden');
        allHidden.forEach(el => el.classList.remove('hidden'));
        
          // Get the inner content
        const labelCard = cloned.querySelector('.label-card');
        if (labelCard) {
          const codeValue = calculateCodeValue(rowData);
          
          // Create new structure matching the design
          const headerDiv = document.createElement('div');
          headerDiv.className = 'label-header';
          
          // Get logo from existing structure or create it
          const existingLogo = labelCard.querySelector('.label-logo');
          if (existingLogo) {
            const logoClone = existingLogo.cloneNode(true);
            headerDiv.appendChild(logoClone);
          } else {
            // Create logo element
            const logoDiv = document.createElement('div');
            logoDiv.className = 'label-logo';
            const logoImg = document.createElement('img');
            logoImg.src = logoImage;
            logoImg.alt = 'GST Logo';
            logoDiv.appendChild(logoImg);
            headerDiv.appendChild(logoDiv);
          }
          
          // Get QR code from existing structure
          const existingQrCode = labelCard.querySelector('.label-qrcode');
          if (existingQrCode) {
            const qrClone = existingQrCode.cloneNode(true);
            headerDiv.appendChild(qrClone);
          } else {
            // Create placeholder if QR code not found
            const qrPlaceholder = document.createElement('div');
            qrPlaceholder.className = 'label-qrcode';
            qrPlaceholder.innerHTML = '<div style="width: 80px; height: 80px; background: #f0f0f0;">QR</div>';
            headerDiv.appendChild(qrPlaceholder);
          }
          
          // Create separator line (inline style forces black in print)
          const separator = document.createElement('div');
          separator.className = 'label-separator';
          separator.setAttribute('style', 'height: 0; border: none; border-top: 2px solid #000000; background-color: #000000; -webkit-print-color-adjust: exact; print-color-adjust: exact;');
          
          // Get barcode container
          const barcodeContainer = labelCard.querySelector('.label-barcode-container');
          const barcodeClone = barcodeContainer ? barcodeContainer.cloneNode(true) : null;
          
          // Create code text
          const codeEl = document.createElement('div');
          codeEl.className = 'label-code';
          codeEl.textContent = codeValue;
          
          // Build new structure
          const newLabelCard = document.createElement('div');
          newLabelCard.className = 'label-card';
          newLabelCard.appendChild(headerDiv);
          newLabelCard.appendChild(separator);
          if (barcodeClone) {
            newLabelCard.appendChild(barcodeClone);
          } else {
            const barcodePlaceholder = document.createElement('div');
            barcodePlaceholder.className = 'label-barcode-container';
            barcodePlaceholder.innerHTML = `<div style="height: 50px; width: 250px; background: #f0f0f0;">Barcode: ${codeValue}</div>`;
            newLabelCard.appendChild(barcodePlaceholder);
          }
          newLabelCard.appendChild(codeEl);
          
          printContent = newLabelCard.outerHTML;
        } else {
          printContent = cloned.innerHTML;
        }
      }
      
      // Fallback if source not found or content is empty
      if (!printContent) {
        const codeValue = calculateCodeValue(rowData);
        const logoPath = logoImage;
        
        printContent = `
          <div class="label-card">
            <div class="label-header">
              <div class="label-logo">
                <img src="${logoPath}" alt="GST Logo" />
              </div>
              <div class="label-qrcode">
                <div style="width: 80px; height: 80px; background: #f0f0f0; display: flex; align-items: center; justify-content: center; font-size: 8px; color: #666;">QR</div>
              </div>
            </div>
            <div class="label-separator" style="height: 0; border: none; border-top: 2px solid #000000; background-color: #000000; -webkit-print-color-adjust: exact; print-color-adjust: exact;"></div>
            <div class="label-barcode-container">
              <div style="height: 50px; width: 250px; background: #f0f0f0; display: flex; align-items: center; justify-content: center; font-size: 10px; color: #666;">Barcode: ${codeValue}</div>
            </div>
            <div class="label-code">${codeValue}</div>
          </div>
        `;
      }

      printWindow.document.write(`
        <html>
          <head>
            <title>Location Tag - ${rowData.tagNo || ''}</title>
            <style>${styles}</style>
          </head>
          <body>
            <div id="barcode-wrapper">
              ${printContent}
            </div>
          </body>
        </html>
      `);

      printWindow.document.close();
      
      // Ensure logo images load properly in print window
      setTimeout(() => {
        const logoImages = printWindow.document.querySelectorAll('.label-logo img');
        logoImages.forEach(img => {
          if (img.src && !img.complete) {
            img.onload = () => {
              console.log('Logo loaded in print window');
            };
            img.onerror = () => {
              console.error('Logo failed to load, trying alternative path');
              // Try using the imported logo path directly
              const logoImg = new Image();
              logoImg.src = logoImage;
              logoImg.onload = () => {
                img.src = logoImg.src;
              };
            };
          }
        });
      }, 100);
      
      // Wait for content to render before printing
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        setTimeout(() => {
          printWindow.close();
        }, 1000);
      }, 1000);
    }, 200); // Small delay to ensure React has rendered the barcode elements
  };

  // Helper function to calculate the code value (used by both barcode and QR code)
  const calculateCodeValue = (row) => {
    const tagNoParts = row.tagNo ? String(row.tagNo).split('-') : [];
    const ctrNoValue = tagNoParts[0] || '';
    const digitalValue = tagNoParts[1] || '';
    
    // Get shortName from row data - check multiple sources to ensure we get the right value
    // Priority: row.name (from API) > row.originalData?.shortName > shortName state
    const shortNameValue = row.name || row.originalData?.shortName || shortName || '';
    
    console.log('calculateCodeValue - Input:', {
      tagNo: row.tagNo,
      name: row.name,
      originalDataShortName: row.originalData?.shortName,
      shortNameState: shortName,
      ctrNoValue,
      digitalValue,
      shortNameValue
    });
    
    // Format: SHORTNAME-CTRNO-DIGITAL (e.g., "MGR-1-1")
    const codeValue = shortNameValue && ctrNoValue && digitalValue
      ? `${shortNameValue.toUpperCase()}-${ctrNoValue}-${digitalValue}`
      : shortNameValue && ctrNoValue
      ? `${shortNameValue.toUpperCase()}-${ctrNoValue}`
      : String(row.tagNo || '').toUpperCase();
    
    const finalValue = codeValue.trim();
    console.log('calculateCodeValue - Output:', finalValue);
    
    return finalValue;
  };

  const columnsWithPrint = [
    ...locationTagColumn(t),
    {
      field: 'print',
      headerName: t('Print'),
      width: 120,
      renderCell: (params) => (
        <button
          type="button"
          className="px-3 py-1 bg-[teal] hover:bg-[rgb(0,94,94)] rounded-md text-white font-bold text-xs tracking-wider"
          onClick={(e) => {
            e.stopPropagation();
            // Pass both the row ID and the full row data to ensure correct row is printed
            handleRowPrint(String(params.row.id), params.row);
          }}
        >
          {t('Print')}
        </button>
      ),
    },
  ];

  return (
    <>
      <style>{`
        .MuiDataGrid-row.generated-row {
          background-color: #fff3cd !important;
        }
        .MuiDataGrid-row.generated-row:hover {
          background-color: #ffe69c !important;
        }
        .MuiDataGrid-row.generated-row.Mui-selected {
          background-color: #ffd700 !important;
        }
      `}</style>
      <div
        className={`flex locTagContainer w-full ${
          i18n.language === "ar" ? "flex-row-reverse" : "flex-row"
        }`}
      >
      <form onSubmit={(e) => e.preventDefault()}>
        {locationTagInputs.map((input) => {
          return (
            <div className="formInput" key={input.id}>
              <label
                htmlFor={input.name}
                className={`flex items-center space-x-2.5 ${
                  i18n.language === "ar"
                    ? "direction-rtl"
                    : "text-start direction-ltr"
                }`}
              >
                {input.label}
              </label>
              <input
                type={input.type}
                placeholder={input.placeholder}
                name={input.name}
                id={input.name}
                className={`w-full py-1 !border-b-1  text-gray-500 focus:outline-none focus:border-[#ffffff]  focus:border-b-[#00A3A3] transition duration-300 ease-in-out border-white border-b-[#00A3A3] ${
                  i18n.language === "ar" ? "text-end" : "text-start"
                }`}
                onChange={
                  input.name === "ctr"
                    ? (e) => setCtr(e.target.value)
                    : input.name === "shortName"
                    ? (e) => setShortName(e.target.value)
                    : input.name === "longName"
                    ? (e) => setCtr2(e.target.value)
                    : input.name === "noOfLabels"
                    ? (e) => setNoOfLabels(e.target.value)
                    : () => {}
                }
                value={
                  input.name === "ctr"
                    ? ctr
                    : input.name === "shortName"
                    ? shortName
                    : input.name === "longName"
                    ? ctr2
                    : input.name === "noOfLabels"
                    ? noOfLabels
                    : ""
                }
              />
            </div>
          );
        })}
        <div className="flex gap-10 justify-center mt-5 mb-5">
          <button
            type="button"
            onClick={handleGenerate}
            disabled={isGenerating}
            className="h-10 w-28 bg-[#4CAF50] hover:bg-[#45a049] disabled:bg-gray-400 disabled:cursor-not-allowed rounded-md text-white font-bold text-sm tracking-wider"
          >
            {isGenerating ? t("Generating...") : t("Generate")}
          </button>
          <button
            type="button"
            onClick={handleBulkSave}
            disabled={isSaving || !tableData || tableData.filter(row => row.isGenerated === true).length === 0}
            className="h-10 w-24 bg-[#2196F3] hover:bg-[#1976D2] disabled:bg-gray-400 disabled:cursor-not-allowed rounded-md text-white font-bold text-sm tracking-wider"
          >
            {isSaving ? t("Saving...") : t("Save")}
          </button>
        </div>
        {message && (
          <div className={`mt-3 text-center px-4 py-2 rounded-md ${
            messageType === 'error' 
              ? 'bg-red-100 text-red-700 border border-red-300' 
              : 'bg-green-100 text-green-700 border border-green-300'
          }`}>
            {message}
          </div>
        )}
      </form>
      <span className="locationTagTable">
        {/* Location Tags Heading */}
        <div className={`datatableTitle mb-4 ${
          i18n.language === "ar" ? "flex-row-reverse" : "flex-row"
        }`}>
          <h2 className="text-2xl font-semibold text-gray-800">{t("Location Tags")}</h2>
        </div>
        
        {/* Search Input Field */}
        <div className="search-input-wrapper mb-6">
          <div className="relative">
            <svg
              className={`absolute top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 ${
                i18n.language === "ar" ? "right-4" : "left-4"
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder={t("Search Location Tags...")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full py-3 px-12 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-[#00A3A3] focus:ring-2 focus:ring-[#00A3A3]/20 transition-all duration-300 ease-in-out bg-white shadow-sm hover:border-gray-300 ${
                i18n.language === "ar" ? "text-end pr-12 pl-4" : "text-start pl-12 pr-4"
              }`}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className={`absolute top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors ${
                  i18n.language === "ar" ? "left-4" : "right-4"
                }`}
                type="button"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
        
        {isLoading && (
          <div className="text-center py-4 text-gray-500">
            {t("Loading...")}
          </div>
        )}
        <Datatable
          secondaryColor="secondary"
          buttonVisibility={false}
          data={tableData}
          columnsName={columnsWithPrint}
          title=""
          uniqueId="locationTagsId"
          deleteBtnEndPoint="/DeleteLocationTagById"
          actionColumnVisibility={true}
          onImportSuccess={() => {
            fetchLocationTags(searchTerm);
          }}
          getRowClassName={(params) => {
            // Style generated rows with a different background color
            return params.row.isGenerated ? 'generated-row' : '';
          }}
        />
      </span>

      <div id="location-barcodes" className="hidden">
        {tableData.map((row, index) => {
          // Ensure ID is always a string for consistent matching
          const uniqueId = String(row.id || `temp-${index}`);
          
          // Calculate the code value ONCE for this row - use it for both barcode and QR code
          const codeValue = row.tagNo ? calculateCodeValue(row) : '';
          
          // Log the value to verify it matches QR code
          if (codeValue) {
            console.log(`Row ${row.id} - Barcode/QR Code Value: "${codeValue}"`);
          }
          
          return (
            <div id={`barcode-${uniqueId}`} className="hidden" key={uniqueId}>
              <div className="label-card">
                <div className="label-header">
                  <div className="label-logo">
                    <img src={logoImage} alt="GST Logo" />
                  </div>
                  <div className="label-qrcode">
                    {row.tagNo && codeValue ? (
                      <QRCodeSVG
                        value={codeValue}
                        height={60}
                        width={60}
                      />
                    ) : null}
                  </div>
                </div>
                <div className="label-separator"></div>
                <div className="label-barcode-container">
                  {row.tagNo && codeValue ? (
                    <Barcode
                      value={codeValue}
                      format="CODE128"
                      height={40}
                      width={3}
                      margin={4}
                      // fontSize={10}
                      displayValue={false}
                      background="#ffffff"
                      lineColor="#000000"
                      valid={function(valid) {
                        console.log('Barcode validation - Value:', codeValue, 'Valid:', valid);
                        if (!valid) {
                          console.error('Invalid barcode value:', codeValue);
                        }
                      }}
                    />
                  ) : (
                    <div style={{ padding: '10px', color: '#999' }}>No barcode data</div>
                  )}
                </div>
                <div className="label-code">{codeValue}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
    </>
  );
}

export default LocationTags